import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../services/api';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/outline';

// Updated component with translation support for car makes and models
export default function CarDataManagement() {
  const { t } = useLanguage();
  const [carMakes, setCarMakes] = useState([]);
  const [carModels, setCarModels] = useState([]);
  const [selectedMake, setSelectedMake] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showMakeModal, setShowMakeModal] = useState(false);
  const [showModelModal, setShowModelModal] = useState(false);
  const [editingMake, setEditingMake] = useState(null);
  const [editingModel, setEditingModel] = useState(null);
  const [makeForm, setMakeForm] = useState({ name: '', nameAr: '', isActive: true });
  const [modelForm, setModelForm] = useState({ name: '', nameAr: '', carMakeId: '', isActive: true });

  useEffect(() => {
    fetchCarMakes();
  }, []);

  const fetchCarMakes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/car-data/makes', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarMakes(response.data);
    } catch (error) {
      console.error('Error fetching car makes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCarModels = async (makeId) => {
    if (!makeId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/car-data/makes/${makeId}/models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarModels(response.data);
    } catch (error) {
      console.error('Error fetching car models:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMakeSelect = (make) => {
    setSelectedMake(make);
    fetchCarModels(make.id);
  };

  const handleCreateMake = async (e) => {
    e.preventDefault();
    console.log('Creating car make:', makeForm);
    try {
      const token = localStorage.getItem('token');
      console.log('Sending request to create car make...');
      const response = await api.post('/api/car-data/makes', makeForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Car make created successfully:', response.data);
      setShowMakeModal(false);
      setMakeForm({ name: '', nameAr: '', isActive: true });
      fetchCarMakes();
    } catch (error) {
      console.error('Error creating car make:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.details || 'Error creating car make';
      alert(errorMessage);
    }
  };

  const handleUpdateMake = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await api.put(`/api/car-data/makes/${editingMake.id}`, makeForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowMakeModal(false);
      setEditingMake(null);
      setMakeForm({ name: '', nameAr: '', isActive: true });
      fetchCarMakes();
    } catch (error) {
      console.error('Error updating car make:', error);
      alert(error.response?.data?.message || 'Error updating car make');
    }
  };

  const handleDeleteMake = async (make) => {
    if (!window.confirm(`Are you sure you want to delete "${make.name}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/car-data/makes/${make.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCarMakes();
      if (selectedMake?.id === make.id) {
        setSelectedMake(null);
        setCarModels([]);
      }
    } catch (error) {
      console.error('Error deleting car make:', error);
      alert(error.response?.data?.message || 'Error deleting car make');
    }
  };

  const handleCreateModel = async (e) => {
    e.preventDefault();
    console.log('Creating car model:', modelForm);
    try {
      const token = localStorage.getItem('token');
      console.log('Sending request to create car model...');
      const response = await api.post('/api/car-data/models', modelForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Car model created successfully:', response.data);
      setShowModelModal(false);
      setModelForm({ name: '', nameAr: '', carMakeId: '', isActive: true });
      if (selectedMake) {
        fetchCarModels(selectedMake.id);
      }
    } catch (error) {
      console.error('Error creating car model:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.details || 'Error creating car model';
      alert(errorMessage);
    }
  };

  const handleUpdateModel = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await api.put(`/api/car-data/models/${editingModel.id}`, modelForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModelModal(false);
      setEditingModel(null);
      setModelForm({ name: '', nameAr: '', carMakeId: '', isActive: true });
      if (selectedMake) {
        fetchCarModels(selectedMake.id);
      }
    } catch (error) {
      console.error('Error updating car model:', error);
      alert(error.response?.data?.message || 'Error updating car model');
    }
  };

  const handleDeleteModel = async (model) => {
    if (!window.confirm(`Are you sure you want to delete "${model.name}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/car-data/models/${model.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (selectedMake) {
        fetchCarModels(selectedMake.id);
      }
    } catch (error) {
      console.error('Error deleting car model:', error);
      alert(error.response?.data?.message || 'Error deleting car model');
    }
  };

  const openMakeModal = (make = null) => {
    if (make) {
      setEditingMake(make);
      setMakeForm({ name: make.name, nameAr: make.nameAr || '', isActive: make.isActive });
    } else {
      setEditingMake(null);
      setMakeForm({ name: '', nameAr: '', isActive: true });
    }
    setShowMakeModal(true);
  };

  const openModelModal = (model = null) => {
    if (model) {
      setEditingModel(model);
      setModelForm({ name: model.name, nameAr: model.nameAr || '', carMakeId: model.carMakeId, isActive: model.isActive });
    } else {
      setEditingModel(null);
      setModelForm({ name: '', nameAr: '', carMakeId: selectedMake?.id || '', isActive: true });
    }
    setShowModelModal(true);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('carData.title')}</h1>
        <p className="mt-2 text-sm text-gray-600">{t('carData.description')}</p>
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">{t('carData.translationNote')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Car Makes Section */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">{t('carData.carMakes')}</h2>
            <button
              onClick={() => openMakeModal()}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {t('carData.addMake')}
            </button>
          </div>

          {loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {carMakes.map((make) => (
                <div
                  key={make.id}
                  className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedMake?.id === make.id
                      ? 'bg-primary-50 border-primary-200'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                  onClick={() => handleMakeSelect(make)}
                >
                  <div className="flex items-center">
                    <span className="font-medium text-gray-900">{make.name}</span>
                    {!make.isActive && (
                      <span className="ml-2 px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openMakeModal(make);
                      }}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMake(make);
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Car Models Section */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">
              {t('carData.carModels')} {selectedMake && `- ${selectedMake.name}`}
            </h2>
            <button
              onClick={() => openModelModal()}
              disabled={!selectedMake}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {t('carData.addModel')}
            </button>
          </div>

          {!selectedMake ? (
            <div className="text-center py-8 text-gray-500">
              {t('carData.selectCarMake')}
            </div>
          ) : loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {carModels.map((model) => (
                <div
                  key={model.id}
                  className="flex items-center justify-between p-3 border rounded-lg bg-gray-50 border-gray-200"
                >
                  <div className="flex items-center">
                    <span className="font-medium text-gray-900">{model.name}</span>
                    {!model.isActive && (
                      <span className="ml-2 px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openModelModal(model)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteModel(model)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Make Modal */}
      {showMakeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editingMake ? t('carData.editCarMake') : t('carData.addCarMake')}
            </h3>
            <form onSubmit={editingMake ? handleUpdateMake : handleCreateMake}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('carData.nameEnglish')}</label>
                <input
                  type="text"
                  value={makeForm.name}
                  onChange={(e) => setMakeForm({ ...makeForm, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('carData.nameArabic')}</label>
                <input
                  type="text"
                  value={makeForm.nameAr}
                  onChange={(e) => setMakeForm({ ...makeForm, nameAr: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  dir="rtl"
                  placeholder={t('carData.arabicPlaceholder')}
                />
              </div>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={makeForm.isActive}
                    onChange={(e) => setMakeForm({ ...makeForm, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  Active
                </label>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowMakeModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  {editingMake ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Model Modal */}
      {showModelModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editingModel ? t('carData.editCarModel') : t('carData.addCarModel')}
            </h3>
            <form onSubmit={editingModel ? handleUpdateModel : handleCreateModel}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('carData.carMake')}</label>
                <select
                  value={modelForm.carMakeId}
                  onChange={(e) => setModelForm({ ...modelForm, carMakeId: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                >
                  <option value="">{t('carData.selectMake')}</option>
                  {carMakes.map((make) => (
                    <option key={make.id} value={make.id}>
                      {make.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('carData.modelNameEnglish')}</label>
                <input
                  type="text"
                  value={modelForm.name}
                  onChange={(e) => setModelForm({ ...modelForm, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('carData.modelNameArabic')}</label>
                <input
                  type="text"
                  value={modelForm.nameAr}
                  onChange={(e) => setModelForm({ ...modelForm, nameAr: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  dir="rtl"
                  placeholder={t('carData.arabicModelPlaceholder')}
                />
              </div>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={modelForm.isActive}
                    onChange={(e) => setModelForm({ ...modelForm, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  Active
                </label>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowModelModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  {editingModel ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 